import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import type { PerformanceConflict } from '../model/recital';

interface ResolveConflictResponse {
  message: string;
  conflict: PerformanceConflict;
}

export interface ResolveConflictParams {
  conflictId: string;
  recitalId: string;
}

export const resolveConflictAPI = async (conflictId: string) => {
  const response = await baseAPI.patch<
    ResolveConflictResponse | PerformanceConflict
  >(`/recitals/conflicts/${conflictId}/resolve`);
  const data = response.data as ResolveConflictResponse & PerformanceConflict;
  // Controller returns { message, conflict }; support either shape
  return data.conflict ?? data;
};

export const useResolveConflictMutation = () => {
  return useMutation({
    mutationFn: async ({ conflictId }: ResolveConflictParams) => {
      return await resolveConflictAPI(conflictId);
    },
    onSuccess: (resolvedConflict, { conflictId, recitalId }) => {
      const effectiveRecitalId =
        (typeof resolvedConflict?.recitalId === 'string'
          ? resolvedConflict.recitalId
          : undefined) ?? recitalId;

      // Immediate UI update so Resolved badge appears without changing filters
      queryClient.setQueriesData<Array<PerformanceConflict>>(
        { queryKey: [KEYS.CONFLICTS, effectiveRecitalId] },
        (old) => {
          if (!old) return old;
          return old.map((conflict) =>
            conflict._id === conflictId || conflict._id === resolvedConflict?._id
              ? {
                  ...conflict,
                  ...resolvedConflict,
                  resolved: true,
                  resolvedAt:
                    resolvedConflict?.resolvedAt ?? new Date().toISOString(),
                }
              : conflict,
          );
        },
      );

      void queryClient.invalidateQueries({
        queryKey: [KEYS.CONFLICTS, effectiveRecitalId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.RECITALS, effectiveRecitalId],
      });
    },
  });
};
